import { FeatureFlag } from '@ps/web-core-enums';
import classNames from 'clsx';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import { DownloadBanner, Navigation, TabView, useSaveLastUsedPath } from '@swf/poker';

import { NAVIGATION_TABS } from '../../constants';
import type { NavigationTabTypes } from '../../types';
import {
  NAVIGATION_TABS_TYPE_URL_MAP,
  NAVIGATION_TABS_URL_TYPE_MAP,
  NavigationTabs
} from '../../types';
import More from '../More/More';
import Overview from '../Overview/Overview';
import ThePasses from '../ThePasses/ThePasses';
import ThePath from '../ThePath/ThePath';

import { TXTCLI_If_you_enjoy_Power_PathX_downl_ELL } from './PowerPath.messages.json';
import styles from './PowerPath.scss';

const PowerPath: React.FC = () => {
  const hideOverviewPage = useFeature(FeatureFlag.PKRPowerPathHideOverviewPage);
  const hideMoreEnable = useFeature(FeatureFlag.PKRPowerPathHideMoreTab);

  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isEmbedded = useSelector(select.device.isEmbedded);

  const navigationTabs = useMemo(
    () =>
      NAVIGATION_TABS.filter((tab) => {
        const showOverview = !hideOverviewPage || tab.tabName !== NavigationTabs.overview;
        const showMore = !hideMoreEnable || tab.tabName !== NavigationTabs.more;

        return showOverview && showMore;
      }),
    [hideOverviewPage, hideMoreEnable]
  );
  const { tab: activeTab } = useSelector(select.cms.getUrlParams);
  const [selectedTab, setSelectedTab] = useState<NavigationTabTypes>(
    NAVIGATION_TABS_URL_TYPE_MAP[activeTab as keyof typeof NAVIGATION_TABS_URL_TYPE_MAP] ||
      NavigationTabs.thePath
  );
  const setUrlPath = useSaveLastUsedPath();

  const onTabSelected = (tabName: NavigationTabTypes) => {
    setSelectedTab(tabName);
    setUrlPath(NAVIGATION_TABS_TYPE_URL_MAP[tabName]);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.navigation}>
            <Navigation
              navTabs={navigationTabs}
              selectedTab={selectedTab}
              onSelectTab={onTabSelected}
              preserveQueryParams={isDesktopEmbedded}
            />
          </div>
          <TabView activeTab={selectedTab}>
            {!hideOverviewPage && (
              <TabView.Tab name={NavigationTabs.overview}>
                <Overview onSelectTab={onTabSelected} />
              </TabView.Tab>
            )}
            <TabView.Tab name={NavigationTabs.thePath}>
              <div
                className={classNames(styles.innerContainer, {
                  [styles.embedded]: isDesktopEmbedded
                })}
              >
                <ThePath />
              </div>
            </TabView.Tab>
            <TabView.Tab name={NavigationTabs.thePasses}>
              <div className={styles.innerContainer}>
                <ThePasses />
              </div>
            </TabView.Tab>
            {!hideMoreEnable && (
              <TabView.Tab name={NavigationTabs.more}>
                <div className={styles.innerContainer}>
                  <More />
                </div>
              </TabView.Tab>
            )}
          </TabView>
        </div>
        {!isEmbedded && (
          <DownloadBanner isOnboardingFinished label={TXTCLI_If_you_enjoy_Power_PathX_downl_ELL} />
        )}
      </div>
    </div>
  );
};

export default PowerPath;
